import React, {
  useState,
  useRef,
  type SetStateAction,
  type RefObject,
  type Dispatch,
  useEffect,
} from 'react';
import { useClickOutside } from '@lili/utils/hooks';
import { SelectMenuSearchInput } from './SelectMenuSearchInput';
import { SelectMenuList } from './SelectMenuList';
import type { MenuItem, LiliComboboxWithInputValueConstraint } from './SelectMenuItem';
import { filterItems } from '../util';
import { twMerge } from 'tailwind-merge';

interface SelectMenuProps<T extends LiliComboboxWithInputValueConstraint> {
  id: string;
  items: MenuItem<T>[];
  selectedValue: T;
  onItemSelected: (value: T) => void;
  setIsSelectOpen: Dispatch<SetStateAction<boolean>>;
  elTriggerButton: RefObject<HTMLButtonElement | null>;
  inputPlaceholder?: string;
  InputPrefixIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  className?: string;
}

export const SelectMenu = <T extends LiliComboboxWithInputValueConstraint>({
  id,
  items,
  selectedValue,
  onItemSelected,
  setIsSelectOpen,
  elTriggerButton,
  inputPlaceholder,
  InputPrefixIcon,
  className,
}: SelectMenuProps<T>) => {
  const [filterKeyword, setFilterKeyword] = useState('');
  const elMenu = useRef<HTMLDivElement>(null);
  const elSearchInput = useRef<HTMLInputElement>(null);

  const filteredItems = filterItems(items, filterKeyword);

  const onClickOutside = () => setIsSelectOpen(false);

  useClickOutside({
    el: elMenu,
    excludeEls: [elTriggerButton],
    onClickOutside,
  });

  const handleItemSelection = (value: T) => {
    onItemSelected(value);
    setIsSelectOpen(false);
  };

  // Focus the search input when the component mounts
  useEffect(() => {
    if (elSearchInput.current) {
      elSearchInput.current.focus();
    }
  }, []);

  // Listen for escape key to close the menu
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setIsSelectOpen]);

  return (
    <div
      translate='no'
      ref={elMenu}
      className={twMerge(
        'flex flex-col shadow-select-menu z-[999999999] absolute left-0 bottom-0 translate-y-[calc(100%+5px)] w-full border border-outline-primary-bw-00 rounded-[8px] py-[8px] fadeIn bg-surface-primary-bw-00',
        className
      )}>
      <SelectMenuSearchInput
        ref={elSearchInput}
        id={id}
        value={filterKeyword}
        onValueChange={setFilterKeyword}
        placeholder={inputPlaceholder}
        PrefixIcon={InputPrefixIcon}
      />
      <SelectMenuList
        id={id}
        items={filteredItems}
        selectedValue={selectedValue}
        onItemClick={handleItemSelection}
        searchWord={filterKeyword}
      />
    </div>
  );
};
